import React, { useState, useEffect } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import styled from 'styled-components';

import { Layout, Box, Flex, Text } from '../../components';
import { TextInput } from '../../components/formik-fields';
import { Button } from '../../components/enhanced/Button';
import Modal from '../../components/enhanced/Modal';
import { Dialog } from '../../components/dialog';
import { BarWrapper } from '../../components/topbar/bar-wrapper';
import { FileInput } from '../../components/abstract/forms/file-input';
import { ErrorMessage } from '../../components/abstract/styled/forms/common-styles';
import { registerTaxIdCNPJ, getFileUploadURL } from '../../services/taxids';
import appConstants from '../../constants/appConstants';


const FormRow = props => <Box mb={3} {...props} />;

const InputField = styled(Field)`
  width: ${({ width }) => width}%;

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const HelpText = styled.span`
  color: #82878e;
  font-size: 0.8rem;
`;

const FORM_SUBMITTED = 1;
const FORM_UPLOADING_FILES = 2;
const FORM_REGISTERING_TAX_ID = 3;

const statusMessages = {
  [FORM_UPLOADING_FILES]: 'Enviando documentos...',
  [FORM_REGISTERING_TAX_ID]: 'Registrando novo perfil...',
  [FORM_SUBMITTED]: '',
};

const cnpjRegex = /^[0-9]{2}\.[0-9]{3}\.[0-9]{3}\/[0-9]{4}-[0-9]{2}$/;

const CnpjSchema = Yup.object().shape({
  companyName: Yup.string().required('Razão social é obrigatória'),
  cnpj: Yup.string().matches(cnpjRegex, 'Formato inválido').required('CNPJ é obrigatório'),
  nickName: Yup.string()
    .max(10, 'O Apelido deve ter no máximo 10 caracteres')
    .required('O Apelido é obrigatório'),
  docs: Yup.array()
    .min(1, 'Deve ser anexado pelo menos um documento')
    .max(4, 'No máximo 4 documentos podem ser anexados')
    .nullable()
    .required('Deve ser anexado pelo menos um documento'),
});

export const CnpjProfileForm = () => {
  const token = useSelector(state => state.authentication.token?.access_token);
  const userId = useSelector(state => state.authentication.user.id);
  const queryClient = useQueryClient();
  const history = useHistory();

  const [status, setStatus] = useState(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [createdProfileId, setCreatedProfileId] = useState(null);

  const mutation = useMutation(registerTaxIdCNPJ, {
    onSuccess: data => {
      queryClient.invalidateQueries('taxids');
      setCreatedProfileId(data?.id || null);
      setStatus(FORM_SUBMITTED);
    },
    onError: err => {
      const msg = err?.response?.data?.message || err.message || 'Erro ao cadastrar perfil';
      setErrorMessage(msg);
      setShowErrorAlert(true);
      setStatus(null);
    },
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setStatus(FORM_UPLOADING_FILES);
      const docRequests = values.docs.map(async doc => {
        const fileExtension = doc.name.match(/\.([0-9a-z]+)$/i)[1];
        const uploadResponse = await getFileUploadURL(userId, fileExtension);
        const { key, uploadURL } = uploadResponse.data.data;
        await fetch(uploadURL, {
          method: 'PUT',
          body: doc,
        });
        return key;
      });

      const uploadedKeys = await Promise.all(docRequests);

      // Cadastro no backend
      setStatus(FORM_REGISTERING_TAX_ID);
      await mutation.mutateAsync({
        type: appConstants.TaxIdType.CNPJ,
        socialReason: values.companyName,
        taxIdentificationNumber: values.cnpj,
        fantasyName: values.fantasyName,
        nickName: values.nickName,
        supportImages: uploadedKeys,
      });

      resetForm();
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorAlert(true);
      setStatus(null);
    }
  };

  useEffect(() => {
    if (status === FORM_SUBMITTED) {
      const timer = setTimeout(() => setStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  return (
    <Layout>
      <Flex flexDirection="column" width={1}>
        <BarWrapper
          color="primaries.Taxi"
          height={{ sm: '2.5rem', md: '3.7rem' }}
          fontSize={{ sm: 2, md: 5 }}
          px={1}
          css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
        >
          Novo cadastro de dados para emissão do DUT e da Nota Fiscal
        </BarWrapper>

        <Box className="container" mx="auto" p={3} width={1} css={{ maxWidth: '960px' }}>
          <Formik
            initialValues={{ companyName: '', fantasyName: '', cnpj: '', nickName: '', docs: null }}
            validationSchema={CnpjSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <>
                <Form>
                  <FormRow>
                    <InputField component={TextInput} id="companyName" name="companyName" labelText="Razão Social" width="50" />
                  </FormRow>
                  <FormRow>
                    <InputField component={TextInput} id="fantasyName" name="fantasyName" labelText="Nome Fantasia" width="50" />
                  </FormRow>
                  <FormRow>
                    <InputField component={TextInput} id="cnpj" name="cnpj" labelText="CNPJ" placeholder="00.000.000/0001-00" width="50" />
                  </FormRow>
                  <FormRow>
                    <InputField
                      component={TextInput}
                      id="nickName"
                      name="nickName"
                      labelText="Apelido"
                      maxLength={10}
                      width="50"
                      formText="O apelido será utilizado para identificar seu perfil."
                    />
                  </FormRow>

                  <FormRow>
                    <Field name="docs">
                      {({ field, form: { setFieldValue }, meta }) => (
                        <>
                          <FileInput
                            id="tax-id-docs"
                            labelText="Documentos da empresa"
                            multiple
                            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                            onChange={files => setFieldValue(field.name, files)}
                          />
                          <HelpText>*Arquivos aceitos: .png, .jpg, .jpeg, .pdf, .doc e .docx</HelpText>
                          {meta.touched && meta.error && <ErrorMessage>{meta.error}</ErrorMessage>}
                        </>
                      )}
                    </Field>
                  </FormRow>

                  <Flex justifyContent="flex-end" flexWrap="wrap">
                    <Button as={Link} to="/perfis" variant="ghost.primary" replace>
                      Cancelar
                    </Button>
                    <Button
                      ml={2}
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !isValid || !dirty || status === FORM_SUBMITTED}
                    >
                      Enviar para validação
                    </Button>
                  </Flex>
                </Form>

                <Modal show={status === FORM_SUBMITTED || isSubmitting}>
                  {isSubmitting ? (
                    <Flex p={4} minWidth={300} flexDirection="column" alignItems="center" justifyContent="center">
                      <Text mt={1} fontSize={1}>
                        {statusMessages[status]}
                      </Text>
                    </Flex>
                  ) : (
                    status === FORM_SUBMITTED && (
                      <Dialog header="Solicitação realizada com sucesso!" onCTAClick={() => history.push('/perfis')}>
                        <p>Prontinho, você acabou de solicitar o cadastro de um novo perfil.</p>
                        {createdProfileId && (
                          <p>
                            <strong>ID do perfil:</strong> {createdProfileId}
                          </p>
                        )}
                        <p>Assim que finalizarmos a validação dos dados, o perfil será habilitado para uso em leilões.</p>
                      </Dialog>
                    )
                  )}
                </Modal>

                <Modal show={showErrorAlert}>
                  <Dialog
                    variant="error"
                    header="Ops, tivemos um problema!"
                    onCTAClick={() => setShowErrorAlert(false)}
                    primaryBtnLabel="Entendi"
                    primaryBtnVariant="error"
                  >
                    <p>Tivemos um problema ao realizar o cadastro</p>
                    <p>{errorMessage}</p>
                  </Dialog>
                </Modal>
              </>
            )}
          </Formik>
        </Box>
      </Flex>
    </Layout>
  );
};

export default CnpjProfileForm;
